import React, { useState, useEffect, useRef, useMemo } from "react";
import { useAuth } from "../../AuthContext";
import { useAxios } from "../../core/_axios";
import { useNavigate, useLocation, Link } from "react-router-dom";
import LoadingSpinner from "../../components/LoadingSpinner";
import usePageTitle from "../../hooks/usePageTitle";
import useReceivedEmailsWebSocket from "../../hooks/useReceivedEmailsWebSocket";
import { usePageHeader } from "../../contexts/PageHeaderContext";
import "./ReceivedEmails.css";

interface ReceivedEmail {
  id: string;
  from: string;
  to: string;
  subject: string;
  status: string;
  received_at: string;
  attachment_count: number;
  text_content?: string;
  thread_id?: string;
  is_reply?: boolean;
  thread_count?: number;
  linked_services_count?: number;
  account?: {
    unique_identifier: string;
    name: string;
  };
}

interface PaginationInfo {
  current_page: number;
  total_pages: number;
  total_count: number;
  per_page: number;
}

interface ReceivedEmailsResponse {
  emails: ReceivedEmail[];
  pagination: PaginationInfo;
  unread_count: number;
  showing_all_accounts?: boolean;
  accounts?: Array<{
    id: string;
    name: string;
    email_count: number;
  }>;
}

const ReceivedEmails: React.FC = () => {
  usePageTitle('Inbox');
  
  const { currentUser, viewingAllAccounts, currentUserAccountUniqueId } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const axios = useAxios();
  const { setPageHeader } = usePageHeader();
  const [emails, setEmails] = useState<ReceivedEmail[]>([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const initialLoadRef = useRef(true);

  // Determine view mode based on current route
  const viewMode: "all" | "threads" = useMemo(() => {
    if (location.pathname.includes('/threads')) return 'threads';
    return 'all';
  }, [location.pathname]);

  // Set up WebSocket for real-time updates
  const { isConnected } = useReceivedEmailsWebSocket({
    onEmailCreated: (newEmail) => {
      // Add new email to the list if it matches current filters
      if (shouldShowEmail(newEmail)) {
        setEmails(prevEmails => [newEmail, ...prevEmails].slice(0, pagination?.per_page || 20));
        setUnreadCount(prev => prev + 1);
      }
    },
    onEmailUpdated: (updatedEmail) => {
      // Update existing email in the list
      setEmails(prevEmails => 
        prevEmails.map(email => 
          email.id === updatedEmail.id ? { ...email, ...updatedEmail } : email
        )
      );
      
      // Update unread count if status changed
      const existingEmail = emails.find(e => e.id === updatedEmail.id);
      if (existingEmail && existingEmail.status === 'unread' && updatedEmail.status !== 'unread') {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    }
  });

  // Memoize navigation items to prevent recreating on every render
  const navigationItems = useMemo(() => (
    <div className="flex gap-4 text-sm">
      <Link
        to="/emails/received/all"
        className={viewMode === 'all' ? "text-primary font-semibold" : "text-gray-600 hover:text-primary"}
      >
        All
      </Link>
      <Link
        to="/emails/received/threads"
        className={viewMode === 'threads' ? "text-primary font-semibold" : "text-gray-600 hover:text-primary"}
      >
        Threads
      </Link>
    </div>
  ), [viewMode]);

  // Set up page header with navigation
  useEffect(() => {
    const titleWithCount = unreadCount > 0 ? `Inbox (${unreadCount})` : 'Inbox';
    setPageHeader(titleWithCount, navigationItems);
    
    return () => setPageHeader(null);
  }, [navigationItems, unreadCount, setPageHeader]);

  useEffect(() => {
    // Only show full loading spinner on very first mount
    const isInitialLoad = initialLoadRef.current;
    if (initialLoadRef.current) {
      initialLoadRef.current = false;
    }
    fetchEmails(isInitialLoad);
  }, [currentPage, statusFilter, searchQuery, viewMode]);

  // Cleanup debounce timer
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  const fetchEmails = async (isInitialLoad = false) => {
    if (isInitialLoad) {
      setLoading(true);
    } else {
      setSearching(true);
    }
    setError(null);
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        ...(statusFilter && { status: statusFilter }),
        ...(searchQuery && { search: searchQuery }),
        view: viewMode,
        ...(viewingAllAccounts && { all_accounts: 'true' })
      });

      const response = await axios.get(`/api/v1/app/authenticated/current-account/received-emails?${params}`);
      const data: ReceivedEmailsResponse = response.data;
      setEmails(data.emails);
      setPagination(data.pagination);
      setUnreadCount(data.unread_count);
    } catch (err: any) {
      setError("Failed to load emails");
      console.error("Failed to load emails:", err);
      console.error("Response:", err.response?.data);
      console.error("Status:", err.response?.status);
    } finally {
      setLoading(false);
      setSearching(false);
    }
  };

  const handleEmailClick = (emailId: string) => {
    navigate(`/emails/received/${emailId}`);
  };

  const shouldShowEmail = (email: ReceivedEmail): boolean => {
    // Check status filter
    if (statusFilter && email.status !== statusFilter) {
      return false;
    }
    
    // Check search query
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch = 
        email.from.toLowerCase().includes(searchLower) ||
        email.to.toLowerCase().includes(searchLower) ||
        (email.subject || '').toLowerCase().includes(searchLower) ||
        (email.text_content || '').toLowerCase().includes(searchLower);
      
      if (!matchesSearch) {
        return false;
      }
    }
    
    // Check view mode
    if (viewMode === 'threads' && !email.thread_id) {
      return false;
    }
    
    return true;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
    } else if (diffInHours < 168) { // 7 days
      return date.toLocaleDateString('en-US', { 
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      });
    } else {
      return date.toLocaleDateString('en-US', { 
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });
    }
  };

  const extractSenderName = (from: string) => {
    // Extract name from "Name <email@example.com>" format
    const match = from.match(/^([^<]+)\s*</);
    if (match) {
      return match[1].trim();
    }
    // If no name, extract the part before @ from email
    const emailMatch = from.match(/^([^@]+)@/);
    return emailMatch ? emailMatch[1] : from;
  };

  const extractSenderEmail = (from: string) => {
    // Extract email from "Name <email@example.com>" format
    const match = from.match(/<([^>]+)>/);
    if (match) {
      return match[1];
    }
    // If already just an email, return it
    return from;
  };

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const highlightMatch = (text: string, query: string): React.ReactNode => {
    if (!query || !text) return text;
    
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <span key={index} className="bg-blue-100 text-blue-700 font-semibold rounded px-0.5">{part}</span>
      ) : (
        part
      )
    );
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="container-fixed">
      <div className="flex items-center justify-end pb-5">
        <div className="flex items-center gap-3">
          <div className="relative">
            <i className="ki-filled ki-magnifier absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"></i>
            <input
              type="text"
              className={`input input-sm pl-10 ${searching || searchQuery ? 'pr-10' : 'pr-4'}`}
              placeholder="Search emails..."
              value={searchInput}
              onChange={(e) => {
                const value = e.target.value;
                setSearchInput(value);
                
                // Debounce search
                if (debounceTimer.current) {
                  clearTimeout(debounceTimer.current);
                }
                
                debounceTimer.current = setTimeout(() => {
                  setSearchQuery(value);
                  setCurrentPage(1);
                }, 300);
              }}
              style={{ width: '250px' }}
            />
            {searching && (
              <div className="absolute right-8 top-1/2 -translate-y-1/2">
                <i className="ki-filled ki-loading animate-spin text-gray-400"></i>
              </div>
            )}
            {searchQuery && !searching && (
              <button
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                onClick={() => {
                  setSearchQuery('');
                  setSearchInput('');
                  setCurrentPage(1);
                }}
              >
                <i className="ki-filled ki-cross-circle"></i>
              </button>
            )}
          </div>
          
          <select
            className="select select-sm pr-8"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">All Emails</option>
            <option value="unread">Unread</option>
            <option value="read">Read</option>
            <option value="archived">Archived</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger mb-5">
          {error}
        </div>
      )}

      <div className={`card gmail-list-container transition-opacity duration-150 ${searching ? 'opacity-60' : 'opacity-100'}`}>
        {emails.length === 0 ? (
          <div className="card-body">
            <div className="text-center py-12 text-gray-500">
              <i className="ki-filled ki-message-text-2 text-6xl mb-4 text-gray-300"></i>
              <p>{searchQuery ? `No emails found matching "${searchQuery}"` : 'No emails found'}</p>
            </div>
          </div>
        ) : (
          <div className="gmail-list">
            {emails.map((email) => (
              <div 
                key={email.id}
                className={`gmail-row ${email.status === 'unread' ? 'unread' : ''}`}
                onClick={() => handleEmailClick(email.id)}
              >
                {/* Action column - commented out for now */}
                {/* <div className="gmail-checkbox">
                  <input type="checkbox" className="checkbox checkbox-sm" onClick={(e) => e.stopPropagation()} />
                </div> */}
                
                {/* Star column - commented out for now */}
                {/* <div className="gmail-star">
                  <i className="ki-outline ki-star text-gray-400 hover:text-yellow-500"></i>
                </div> */}
                
                {/* Sender */}
                <div className="gmail-sender">
                  {searchQuery ? highlightMatch(extractSenderName(email.from), searchQuery) : extractSenderName(email.from)}
                  {viewingAllAccounts && email.account && (
                    <span className="account-badge">{email.account.name}</span>
                  )}
                </div>
                
                {/* Subject and preview */}
                <div className="gmail-content">
                  <span className="gmail-subject">
                    {searchQuery ? highlightMatch(email.subject || "(no subject)", searchQuery) : (email.subject || "(no subject)")}
                  </span>
                  {email.text_content && (
                    <span className="gmail-preview">
                      {searchQuery ? highlightMatch(truncateText(email.text_content.replace(/\n/g, ' '), 100), searchQuery) : truncateText(email.text_content.replace(/\n/g, ' '), 100)}
                    </span>
                  )}
                </div>
                
                {/* Indicators */}
                <div className="gmail-indicators">
                  {viewMode === 'threads' && email.thread_count && email.thread_count > 1 && (
                    <span className="thread-count">{email.thread_count}</span>
                  )}
                  {email.linked_services_count && email.linked_services_count > 0 && (
                    <span className="service-count" title={`Linked to ${email.linked_services_count} service${email.linked_services_count > 1 ? 's' : ''}`}>
                      <i className="ki-filled ki-profile-user"></i>
                      {email.linked_services_count}
                    </span>
                  )}
                  {email.attachment_count > 0 && (
                    <i className="ki-filled ki-paper-clip attachment-icon"></i>
                  )}
                </div>
                
                {/* Date */}
                <div className="gmail-date">
                  {formatDate(email.received_at)}
                </div>
              </div>
            ))}
          </div>
        )}

        {pagination && pagination.total_pages > 1 && (
          <div className="card-footer">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">
                {((pagination.current_page - 1) * pagination.per_page) + 1}–
                {Math.min(pagination.current_page * pagination.per_page, pagination.total_count)} of {pagination.total_count}
              </span>
              <div className="flex gap-2">
                <button
                  className="btn btn-sm btn-icon"
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <i className="ki-filled ki-arrow-left"></i>
                </button>
                <button
                  className="btn btn-sm btn-icon"
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === pagination.total_pages}
                >
                  <i className="ki-filled ki-arrow-right"></i>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReceivedEmails;